import dayjs from "dayjs";
import { reassoirLignePrimeNette, reassoirLigneSinistreRetro } from "./primeNetteCession";

// =============================================================================
// VERSION SIMPLIFIÉE : les répartitions sont pré-calculées côté SQL
// Plus besoin de clesRepartition ni de appliquerCleRepartition()
// =============================================================================

// =============================================================================
// EXCLUSION DE LIGNES (comptes de cession corrigés) — helpers partagés
//   Type ∈ EMISSION | ANNULATION | SINISTRE | RECOURS. Une ligne est identifiée
//   par son codeId (repli numeroPolice / sinistreNumSinistre).
// =============================================================================
export const TYPE_PARAM_MOTIF_EXCLUSION = {
    EMISSION: "MOTIF_EXCLUSION_RETRO_EMISSION",
    ANNULATION: "MOTIF_EXCLUSION_RETRO_ANNULATION",
    SINISTRE: "MOTIF_EXCLUSION_RETRO_SINISTRE",
    RECOURS: "MOTIF_EXCLUSION_RETRO_RECOURS",
};

export const ligneIdOf = (line) =>
    line?.codeId || line?.numeroPolice || line?.sinistreNumSinistre || "";

export const cleLigneRetro = (type, line) => `${type}:${ligneIdOf(line)}`;

// Index des exclusions ACTIVES : Map "TYPE:ligneCodeId" -> exclusion.
export const indexerExclusions = (list = []) => {
    const m = new Map();
    (list || []).forEach((ex) => {
        if (ex?.type && ex?.ligneCodeId) m.set(`${ex.type}:${ex.ligneCodeId}`, ex);
    });
    return m;
};

// Exclusion active pour une ligne (ou null).
export const lookupExclusion = (index, type, line) =>
    (index && index.get) ? (index.get(cleLigneRetro(type, line)) || null) : null;

export const calculerDetailPenalite = (joursRetard, tranches) => {
    if (joursRetard <= 0 || !tranches || tranches.length === 0) {
        return { total: 0, details: [] };
    }

    const details = [];
    let total = 0;

    const tranchesTriees = [...tranches].sort((a, b) => a.joursDebut - b.joursDebut);

    for (const tranche of tranchesTriees) {
        if (joursRetard > tranche.joursDebut) {
            const joursApplicables =
                joursRetard <= tranche.joursFin
                    ? joursRetard - tranche.joursDebut
                    : tranche.joursFin - tranche.joursDebut;

            if (joursApplicables > 0) {
                const montantTranche = joursApplicables * tranche.montantParJour;

                details.push({
                    tranche: `${tranche.joursDebut}-${tranche.joursFin} jours`,
                    joursApplicables,
                    tarifParJour: tranche.montantParJour,
                    montantTranche
                });

                total += montantTranche;
            }
        }
    }

    return { total, details };
};


// Détecte les lignes d'émission d'une cession dont le contrat n'a PAS de date d'effet/échéance
// (non cessible/rétrocédable). Regroupé par cession (cessionId) → {cessionId, compagnie, count, total}.
export const detecterAnomaliesCession = (cessionsParCompagnie) => {
    const map = new Map();
    (cessionsParCompagnie || []).forEach((groupe) => {
        // Détecter sur les émissions BRUTES (avant filtrage retro) : la ligne anomale est écartée de la
        // décompo retro (`emissions`), donc invisible là — mais présente dans `emissionsBrutes`.
        // NB : `effet` peut être pollué (repli sur la date de règlement côté backend) → l'absence
        // d'`echeance` (date du contrat, sans repli) reste le signal fiable de « contrat sans date ».
        const source = (groupe?.emissionsBrutes && groupe.emissionsBrutes.length)
            ? groupe.emissionsBrutes : (groupe?.emissions || []);
        source.forEach((it) => {
            const sansDate = !it?.effet || it.effet === 'N/A' || !it?.echeance || it.echeance === 'N/A';
            if (!sansDate || !it?.cessionId) return;
            if (!map.has(it.cessionId)) {
                map.set(it.cessionId, { cessionId: it.cessionId, compagnie: groupe.compagnie, count: 0, total: 0 });
            }
            const e = map.get(it.cessionId);
            e.count += 1;
            e.total += Number(it.primeNettePool ?? it.totalRetrocede ?? it.primeACeder) || 0;
        });
    });
    return Array.from(map.values());
};

export const grouperCessionsParCompagnie = (cessions, dateLimiteCession, penalites, moisRetrocession, tauxRepartition = null, tauxParExerciceSurvenance = null) => {
    const groupes = new Map();

    cessions.forEach((cession) => {
        const compagnieCode = cession.compagnie.code;

        if (!groupes.has(compagnieCode)) {
            groupes.set(compagnieCode, {
                compagnie: cession.compagnie,
                dateCession: cession.dateEmission,
                // TAUX de l'exercice, portés par le groupe : traiter*Compagnie recompose la répartition
                // de chaque ligne = prime nette × taux (au lieu de la répartition stockée par règlement).
                tauxRepartition,
                // SINISTRES/RECOURS : map { année de survenance → clés }, car le taux dépend de
                // l'exercice de survenance de CHAQUE ligne (pas de l'exercice de cession).
                tauxParExerciceSurvenance,
                cessions: [],
                totalEmissions: 0,
                totalAnnulations: 0,
                totalSinistres: 0,
                totalRecours: 0,
                joursRetard: 0,
                penalite: 0,
                detailsPenalite: null,
                emissions: [],
                emissionsBrutes: [], // avant filtrage retro → détection des anomalies (contrat sans date)
                annulations: [],
                sinistres: [],
                recours: [],
                // Nouveaux champs pré-calculés
                emissionsRetrocedees: [],
                totalEmissionsRetrocedees: 0,
            });
        }

        const groupe = groupes.get(compagnieCode);
        groupe.cessions.push(cession);

        if (cession.bordereauEmissions?.items) {
            cession.bordereauEmissions.items.forEach(item => {
                groupe.emissions.push({
                    ...item,
                    cessionId: cession.codeId,
                    dateCession: cession.dateEmission
                });
            });
        }
        // Émissions brutes (avant filtrage retro) — pour la détection d'anomalies uniquement.
        (cession._emissionsBrutes || []).forEach(item => {
            groupe.emissionsBrutes.push({ ...item, cessionId: cession.codeId });
        });
        groupe.totalEmissions += cession.totalEmissions || 0;

        if (cession.bordereauAnnulations?.items) {
            cession.bordereauAnnulations.items.forEach(item => {
                groupe.annulations.push({
                    ...item,
                    cessionId: cession.codeId,
                    dateCession: cession.dateEmission
                });
            });
        }
        groupe.totalAnnulations += cession.totalAnnulations || 0;

        if (cession.bordereauSinistresPayes?.items) {
            cession.bordereauSinistresPayes.items.forEach(item => {
                groupe.sinistres.push({
                    ...item,
                    cessionId: cession.codeId,
                    dateCession: cession.dateEmission
                });
            });
        }
        groupe.totalSinistres += cession.totalSinistres || 0;

        if (cession.bordereauRecoursEncaisses?.items) {
            cession.bordereauRecoursEncaisses.items.forEach(item => {
                groupe.recours.push({
                    ...item,
                    cessionId: cession.codeId,
                    dateCession: cession.dateEmission
                });
            });
        }
        groupe.totalRecours += cession.totalRecours || 0;

        if (dateLimiteCession && cession.dateEmission) {
            const dateCess = dayjs(cession.dateEmission);
            const dateLimite = dayjs(dateLimiteCession);
            const retard = dateCess.diff(dateLimite, 'day');

            if (retard > 0) {
                groupe.joursRetard = Math.max(groupe.joursRetard, retard);
                groupe.detailsPenalite = calculerDetailPenalite(retard, penalites);
                groupe.penalite = groupe.detailsPenalite?.total || 0;
            }
        }
    });

    // Pré-calcul des émissions rétrocédées pour chaque compagnie
    const resultats = Array.from(groupes.values());
    resultats.forEach((groupe) => {
        // CORRECTION 1 : passer `groupe` (qui a .emissions), pas `groupe.compagnie`
        // CORRECTION 2 : utiliser `moisRetrocession`, pas `groupe.dateCession`
        const { emissionsRetrocedees } = traiterEmissionsCompagnie(groupe, moisRetrocession);
        groupe.emissionsRetrocedees = emissionsRetrocedees;
        groupe.totalEmissionsRetrocedees = emissionsRetrocedees.reduce(
            (sum, e) => sum + (e.montantOriginal || 0), 0
        );
    });

    return resultats;
};


export const appliquerCleRepartition = (montant, dateReference, compagnieCode, clesRepartition) => {
    const cleApplicable = clesRepartition.find(cle =>
        cle.compagnie.code === compagnieCode &&
        dayjs(dateReference).isBetween(dayjs(cle.dateEffet), dayjs(cle.dateEcheance), null, '[]')
    );

    if (!cleApplicable) {
        console.warn(`Aucune clé de répartition trouvée pour ${compagnieCode} à la date ${dateReference}`);
        return { montantRetrocede: 0, tauxApplique: 0 };
    }

    return {
        montantRetrocede: montant * (cleApplicable.taux / 100),
        tauxApplique: cleApplicable.taux
    };
};

// Fonction pour extraire le montant rétrocédé pour une compagnie spécifique
export const getMontantRetrocedeParCompagnie = (item, compagnieCode) => {
    if (!item.repartition || !Array.isArray(item.repartition)) {
        return 0;
    }
    const repartitionCompagnie = item.repartition.find(r => r.compagnieCode === compagnieCode);
    return repartitionCompagnie ? repartitionCompagnie.montant : 0;
};

export const getTauxRetrocedeParCompagnie = (item, compagnieCode) => {
    if (!item.repartition || !Array.isArray(item.repartition)) {
        return 0;
    }
    const repartitionCompagnie = item.repartition.find(r => r.compagnieCode === compagnieCode);
    return repartitionCompagnie ? repartitionCompagnie.taux : 0;
};

// =============================================================================
// FONCTIONS DE TRAITEMENT SIMPLIFIÉES
// Les répartitions sont déjà dans chaque item (repartition + totalRetrocede)
// Plus besoin du paramètre clesRepartition
// =============================================================================

export const traiterEmissionsCompagnie = (compagnie, moisRetrocession) => {
    const emissionsRetrocedees = [];
    const emissionsReportees = [];

    compagnie?.emissions?.forEach(emission => {
        // Ligne sans date d'effet EXPLOITABLE = ANOMALIE (contrat sans date) : NI rétrocédée NI reportée
        // (écartée). Sans ce garde-fou, dayjs(null).year() = NaN et « NaN <= annee » est faux → la ligne
        // basculait à tort dans les « émissions reportées ».
        const effetD = dayjs(emission?.effet);
        if (!emission?.effet || !effetD.isValid()) {
            return;
        }
        const anneeEffet = effetD.year();
        const anneeCession = dayjs(moisRetrocession).year();

        if (anneeEffet <= anneeCession) {
            // ASSIETTE = PRIME NETTE : on réassoit la ligne (montant + répartition) sur la prime nette
            // (hors accessoires/taxes), au lieu du TTC stocké dans totalRetrocede. Cohérent avec le
            // compte de cession (calculerTotauxCession) et l'assiette de rétrocession voulue.
            // Répartition RECOMPOSÉE = prime nette × taux de l'exercice (compagnie?.tauxRepartition).
            const net = reassoirLignePrimeNette(emission, compagnie?.tauxRepartition);
            emissionsRetrocedees.push({
                ...net,
                montantOriginal: net.totalRetrocede || 0,
                montantRetrocede: net.totalRetrocede || 0,
                compagnie: compagnie.compagnie
            });
        } else {
            // REPORTÉE (effet d'exercice futur) : on la réassoit aussi sur la prime nette et on la tague
            // par cédante → permet de totaliser la prime reportée par compagnie (compte de cession :
            // vrai cédé = total cédé − reportée). La répartition n'est pas utilisée (non distribuée).
            const net = reassoirLignePrimeNette(emission, compagnie?.tauxRepartition);
            emissionsReportees.push({
                ...net,
                montantOriginal: net.totalRetrocede || 0,
                montantRetrocede: net.totalRetrocede || 0,
                compagnie: compagnie.compagnie
            });
        }
    });

    return { emissionsRetrocedees, emissionsReportees };
};

export const traiterAnnulationsCompagnie = (compagnie, moisRetrocession) => {
    return compagnie.annulations.map(annulation => {
        // ASSIETTE = PRIME NETTE (idem émissions ; les annulations portent des primes négatives).
        // Répartition RECOMPOSÉE = prime nette × taux de l'exercice (compagnie?.tauxRepartition).
        const net = reassoirLignePrimeNette(annulation, compagnie?.tauxRepartition);
        return {
            ...net,
            montantOriginal: Math.abs(net.totalRetrocede || 0),
            montantRetrocede: net.totalRetrocede || 0,
            compagnie: compagnie.compagnie
        };
    });
};

export const traiterSinistresCompagnie = (compagnie, moisRetrocession) => {
    return compagnie.sinistres.map(sinistre => {
        // Répartition RECOMPOSÉE selon l'EXERCICE DE SURVENANCE de la ligne (date_sinistre) quand la
        // répartition stockée est vide — sinon les Taux/Montant restaient à 0 (pas de repli côté SQL).
        const net = reassoirLigneSinistreRetro(sinistre, compagnie?.tauxParExerciceSurvenance);
        return {
            ...net,
            montantOriginal: net.montant,
            montantRetrocede: net.totalRetrocede || 0,
            compagnie: compagnie.compagnie
        };
    });
};

export const traiterRecoursCompagnie = (compagnie, moisRetrocession) => {
    return compagnie.recours.map(recours => {
        // Idem sinistres : le taux dépend de l'exercice de survenance du sinistre lié.
        const net = reassoirLigneSinistreRetro(recours, compagnie?.tauxParExerciceSurvenance);
        return {
            ...net,
            montantOriginal: net.montant,
            montantRetrocede: net.totalRetrocede || 0,
            compagnie: compagnie.compagnie
        };
    });
};

// =============================================================================
// RÉCAPITULATIF GLOBAL (simplifié, plus de clesRepartition)
// =============================================================================

// exclusionIndex (optionnel) : si fourni, les lignes écartées sont RETIRÉES des totaux (récap corrigé).
export const calculerRecapitulatifGlobal = (cessionsParCompagnie, moisRetrocession, exclusionIndex = null) => {
    let totalEmissions = 0;
    let totalAnnulations = 0;
    let totalSinistres = 0;
    let totalRecours = 0;
    let totalPenalites = 0;

    const garder = (type) => (l) => !exclusionIndex || !lookupExclusion(exclusionIndex, type, l);

    cessionsParCompagnie.forEach(compagnie => {
        const { emissionsRetrocedees } = traiterEmissionsCompagnie(compagnie, moisRetrocession);
        const annulationsRetrocedees = traiterAnnulationsCompagnie(compagnie, moisRetrocession);
        const sinistresRetrocedes = traiterSinistresCompagnie(compagnie, moisRetrocession);
        const recoursRetrocedes = traiterRecoursCompagnie(compagnie, moisRetrocession);

        totalEmissions += emissionsRetrocedees.filter(garder("EMISSION")).reduce((sum, e) => sum + (e.montantRetrocede || 0), 0);
        totalAnnulations += annulationsRetrocedees.filter(garder("ANNULATION")).reduce((sum, a) => sum + (a.montantRetrocede || 0), 0);
        totalSinistres += sinistresRetrocedes.filter(garder("SINISTRE")).reduce((sum, s) => sum + (s.montantRetrocede || 0), 0);
        totalRecours += recoursRetrocedes.filter(garder("RECOURS")).reduce((sum, r) => sum + (r.montantRetrocede || 0), 0);
        totalPenalites += compagnie.penalite || 0;
    });

    return {
        totalEmissions,
        totalAnnulations,
        totalSinistres,
        totalRecours,
        totalPenalites,
        solde: (totalEmissions + totalRecours) - (totalAnnulations + totalSinistres) + totalPenalites
    };
};